/* eslint-disable consistent-return */
import {register} from '../vendor/theme-scripts/theme-sections';
import getSiblings from '../util/siblings';

const sections = {};

const selectors = {
  faq: '[data-faq]',
  faqNav: '[data-faq-nav]',
  faqTrigger: '[data-faq-trigger]',
  faqContent: '[data-faq-content]',
  faqAnswerWrapper: '[data-faq-answer-wrapper]',
  faqAnswer: '[data-faq-answer]',
};

const attributes = {
  expanded: 'aria-expanded',
  controls: 'aria-controls',
  hidden: 'aria-hidden',
  type: 'data-type',
  index: 'data-index',
};

const classes = {
  isActive: 'is-active',
};

class FaqList {
  constructor(section) {
    this.container = section.container;
    this.faq = this.container.querySelector(selectors.faq);
    this.resetHeight = 0;
    this.scrollTimeout = 0;

    if (this.faq) {
      this.faqNav = this.faq.querySelector(selectors.faqNav);
      this.faqTriggers = this.faq.querySelectorAll(selectors.faqTrigger);
      this.faqContents = this.faq.querySelectorAll(selectors.faqContent);
      this.faqTriggerClickEvent = (e) => this.faqTriggerClick(e);

      this.init();
    }
  }

  init() {
    this.faqTriggers.forEach((faqTrigger) => {
      faqTrigger.addEventListener('click', this.faqTriggerClickEvent);
      faqTrigger.addEventListener('keyup', this.faqTriggerClickEvent);
    });
  }

  faqTriggerClick(e) {
    e.preventDefault();
    const trigger = e.target.closest(selectors.faqTrigger) || e.target;
    const parent = trigger.parentElement;
    const isActive = parent.classList.contains(classes.isActive);
    const itemsCount = this.faqContents.length;
    const isSpace = e.keyCode === theme.keyboardKeys.SPACE;
    const isEscape = e.keyCode === theme.keyboardKeys.ESCAPE;

    // Do nothing if any different than ESC and Space key pressed
    if (e.keyCode && !isSpace && !isEscape) {
      return;
    }

    if (!isActive && isEscape) {
      return;
    }

    if (trigger.dataset.type == 'tab') {
      // Do nothing if there's a single item on Desktop
      if (itemsCount === 1 && window.innerWidth >= theme.sizes.small) {
        return;
      }
    }

    // Toggle active item
    this.toggleActiveItem(trigger);
  }

  toggleActiveItem(trigger) {
    const parent = trigger.parentElement;
    const isActive = parent.classList.contains(classes.isActive);
    const itemsCount = this.faqContents.length;
    let triggerIndex = parseInt(trigger.getAttribute(attributes.index));

    if (isActive) {
      if (trigger.dataset.type == 'accordion') {
        this.hideItem(trigger);
      }

      if (trigger.dataset.type == 'tab') {
        // Get next element
        triggerIndex += 1;

        // Check if next faq exists or set to first
        if (this.faqTriggers[triggerIndex] === undefined || triggerIndex >= itemsCount) {
          triggerIndex = 0;
        }

        // Open next tab
        this.faqTriggers[triggerIndex].dispatchEvent(new Event('click'));
      }
    } else {
      this.showItem(trigger);
      this.hideSiblings(trigger);
    }
  }

  showItem(trigger) {
    const itemId = trigger.getAttribute(attributes.controls);
    const item = document.getElementById(itemId);
    const parent = item.parentNode;
    const triggerParent = trigger.parentNode;
    const itemInner = item.querySelector(selectors.faqAnswer);
    const itemHeight = itemInner.offsetHeight;
    const isActive = false;

    triggerParent.classList.add(classes.isActive);
    parent.classList.add(classes.isActive);
    parent.querySelector(selectors.faqTrigger).setAttribute(attributes.expanded, true);

    this.setDropdownHeight(item, itemHeight, isActive);

    // Scroll to the expanded item on mobile
    this.scrollToItemOnMobile(item);
  }

  hideItem(trigger) {
    const itemId = trigger.getAttribute(attributes.controls);
    const item = document.getElementById(itemId);
    const itemInner = item.querySelector(selectors.faqAnswer);
    let itemHeight = itemInner.offsetHeight;
    const parent = item.parentNode;
    const triggerParent = trigger.parentNode;
    const isActive = true;

    setTimeout(() => {
      itemHeight = 0;
      this.setDropdownHeight(item, itemHeight, isActive);
    }, 0);

    triggerParent.classList.remove(classes.isActive);
    parent.classList.remove(classes.isActive);
    parent.querySelector(selectors.faqTrigger).setAttribute(attributes.expanded, false);

    this.setDropdownHeight(item, itemHeight, isActive);
  }

  // Close all other active tabs
  hideSiblings(trigger) {
    const parent = trigger.parentElement;
    const siblings = getSiblings(parent);

    if (siblings) {
      siblings.forEach((sibling) => {
        const trigger = sibling.querySelector(selectors.faqTrigger);
        if (sibling.classList.contains(classes.isActive)) {
          this.hideItem(trigger);
        }
      });
    }
  }

  setDropdownHeight(dropdown, dropdownHeight, isActive) {
    dropdown.style.maxHeight = `${dropdownHeight}px`;
    dropdown.setAttribute(attributes.hidden, isActive);
    let maxHeight = 'none';

    if (this.resetHeight) {
      clearTimeout(this.resetHeight);
    }

    if (dropdownHeight === 0) {
      maxHeight = null;
    }

    this.resetHeight = setTimeout(() => {
      dropdown.style.maxHeight = maxHeight;
    }, 250);
  }

  scrollToItemOnMobile(item) {
    if (window.innerWidth < theme.sizes.small || Shopify.designMode) {
      const timeout = Shopify.designMode ? 400 : 250;

      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }

      this.scrollTimeout = setTimeout(() => {
        const titleTop = item.parentElement.getBoundingClientRect().top - document.body.getBoundingClientRect().top - theme.dimensions.headerScrolled;

        window.scrollTo({
          top: titleTop,
          left: 0,
          behavior: 'smooth',
        });
      }, timeout);
    }
  }
}

const FaqListSection = {
  onLoad() {
    sections[this.id] = new FaqList(this);
  },
  onBlockSelect(e) {
    const faqTrigger = e.target.querySelector(selectors.faqTrigger);
    if (!e.target.classList.contains(classes.isActive)) {
      faqTrigger.dispatchEvent(new Event('click'));
    }
  },
  onBlockDeselect(e) {
    const faqTrigger = e.target.querySelector(selectors.faqTrigger);
    if (e.target.classList.contains(classes.isActive) && faqTrigger.getAttribute(attributes.type) == 'accordion') {
      faqTrigger.dispatchEvent(new Event('click'));
    }
  },
};

register('faq', [FaqListSection]);
